import React, { useEffect, useState } from 'react';
import styles from './CciGpt.module.css';
import { Button, Input, message, Modal, Select, Space, Spin, Switch, Table, Typography, Radio, notification, TreeSelect } from 'antd';
import { cciGpt, cciGptService, researchUI } from '../../api';
import { InfoCircleFilled, CloseOutlined } from '@ant-design/icons';
import {
    PLACEHOLDER_TEXT,
    API_METHODS,
    ERROR_MSG,
    SIZE,
    TYPE_OF,
    NOTICATION_MSG,
    ANTD_SELECT,
    ANTD_SPACE,
    COMMON_STRING,
    ANTD_TEXT,
    ANTD_BUTTON,
    COMMON_MSG,
    STATUS_CODE,
    COMMON_NUMBER,
} from '../Helper/Constant';
import { AppOptions, GDPR_Questions } from './HardCoded';
import { CciGptSearch } from './CciGptSearch/CciGptSearch';

const { Text } = Typography;
const { SHOW_PARENT } = TreeSelect;

let AppIdNameMapping = {};
let QuesSummaryMapping = {};
let CategoryQuestionMapping = {};
let AppCategoryList = {};
let QuestionCategoryData = {};

const AllOption = { label: 'All', value: 'all' };
const PriorityOption = [1, 2, 3].map((priority) => {
    return { label: 'P' + priority + ' Apps', value: 'p' + priority };
});

const MinBulkLimit = 3;

const AppTypes = {
    existing: {
        name: 'Existing Applications',
        key: 'existing',
        options: AppOptions,
        placeholder: PLACEHOLDER_TEXT.CCI_GPT_SELECT_APPS,
        customQues: true,
        isProcess: true,
        fetchDropdown: true,
        filterOption: true,
    },
    recentDiscovered: {
        name: 'Recently Discovered Applications',
        options: [],
        placeholder: PLACEHOLDER_TEXT.CCI_GPT_SELECT_APPS,
        customQues: true,
        isProcess: true,
        filterOption: true,
    },
    priority: {
        name: 'Priority-Based Selection',
        options: PriorityOption,
        placeholder: PLACEHOLDER_TEXT.CCI_GPT_SELECT_PRIORITY,
        customQues: false,
        isProcess: false,
    },
    category: {
        name: 'Category-Based Selection',
        options: [],
        placeholder: PLACEHOLDER_TEXT.CCI_GPT_SELECT_CATEGORY,
        customQues: false,
        isProcess: false,
        filterOption: true,
    },
};

const CciGpt = () => {
    // Defaults
    const defaultValues = {
        appType: 'existing',
        apps: [],
        questions: [],
        customQues: COMMON_STRING.BLANK,
        isCustomQues: false,
    };

    // States
    const [loading, setLoading] = useState(true);
    const [reqLoading, setReqLoading] = useState(false);
    const [appOptions, setAppOptions] = useState([]);
    const [questionOptions, setQuestionOptions] = useState([]);
    const [appTypeOption, setAppTypeOption] = useState([]);
    const [showQuesSel, setShowQuesSel] = useState(false);
    const [showResult, setShowResult] = useState(false);
    const [showSwitch, setShowSwitch] = useState(true);
    const [appSelectPlaceholder, setAppSelectPlaceholder] = useState(PLACEHOLDER_TEXT.CCI_GPT_SELECT_APPS);
    const [attriTreeData, setAttriTreeData] = useState([]);
    const [searchModalOpen, setSearchModalOpen] = useState(false);

    const [selAppType, setSelAppType] = useState(defaultValues.appType);
    const [selApps, setSelApps] = useState(defaultValues.apps);
    const [isCustomQues, setIsCustomQues] = useState(defaultValues.isCustomQues);
    const [selQuestions, setSelQuestions] = useState(defaultValues.questions);
    const [customQues, setCustomQues] = useState(defaultValues.customQues);
    const [resultData, setResultData] = useState({});

    const fetchInitData = async () => {
        try {
            const result = await cciGpt({
                function: 'cciGpt',
                getType: 'getCciGptInitData',
            });
            if (result.success == COMMON_NUMBER.API_FAILED) message.error(result.data.msg, 4);
            if (result.success == COMMON_NUMBER.API_SUCCESS) {
                let treeData = result.data.questionCategory.map((quesCat) => {
                    CategoryQuestionMapping[quesCat.category_name] = quesCat.AppIndexQuestions.map((ques) => ques.question_col_name);
                    return {
                        title: quesCat.category_text,
                        value: quesCat.category_name,
                        key: quesCat.category_name,
                        children: quesCat.AppIndexQuestions.map((ques) => {
                            QuesSummaryMapping[ques.question_col_name] = ques.question_summary;
                            const question = { title: ques.question_summary, value: ques.question_col_name, key: ques.question_col_name };
                            if (ques.multi_valued === 1 && !['devicerestrictions', 'SSOADhooks'].includes(ques.question_col_name)) {
                                question.children = ques.AppIndexQuestionChoices.map((choice) => {
                                    QuesSummaryMapping[choice.choice_value] =
                                        choice.choice_text != '' ? choice.choice_text : choice.choice_value;
                                    return {
                                        title: choice.choice_text != '' ? choice.choice_text ?? choice.choice_value : choice.choice_value,
                                        value: choice.choice_value,
                                        key: choice.choice_value,
                                    };
                                });
                            }
                            return question;
                        }),
                    };
                });
                treeData = treeData.concat([
                    {
                        title: 'GDPR',
                        value: 'GDPR',
                        key: 'GDPR',
                        children: GDPR_Questions.map((ques) => {
                            QuesSummaryMapping[ques.value] = ques.label;
                            return {
                                title: ques.label,
                                value: ques.value,
                                key: ques.value,
                            };
                        }),
                    },
                ]);
                setAttriTreeData(treeData);
            }
            CategoryQuestionMapping['GDPR'] = GDPR_Questions.map((ques) => ques.value);
            setLoading(false);
        } catch (error) {
            console.log('Error ', error);
            setLoading(false);
        }
    };

    const fetchAppName = async (appIds, appType) => {
        const result = await cciGpt({
            function: 'cciGpt',
            getType: 'getAppNameByIds',
            appIds: appIds,
        });
        if (result.success == COMMON_NUMBER.API_FAILED) message.error(result.data.msg, 4);
        if (result.success == COMMON_NUMBER.API_SUCCESS) {
            AppIdNameMapping = {};
            const options = result.data.appName.map((name) => {
                AppIdNameMapping[name.application_id] = name.current_name;
                let appName = name.current_name;
                if (appType === 'recentDiscovered') {
                    if (name?.ApplicationCategoryAssoc?.category_id) {
                        appName = name.current_name + ' (' + AppCategoryList[name.ApplicationCategoryAssoc.category_id] + ')';
                    }
                }
                return { label: appName, value: name.application_id };
            });
            AppTypes[appType].options = options;
            if (appType === 'existing') setAppOptions(options);
        }
    };

    const fetchAppIds = async () => {
        try {
            const result = await cciGptService({
                method: API_METHODS.GET,
                uri: 'appid',
                data: {},
            });
            if (result.success == COMMON_NUMBER.API_FAILED) message.error(result.data.msg, 4);
            if (result.data.status_code !== STATUS_CODE.OK) message.error(ERROR_MSG.GENERAL_ERROR, 4);
            if (result.success === COMMON_NUMBER.API_SUCCESS) {
                fetchAppName(result.data.data.existing, 'existing');
                fetchAppName(result.data.data.recentDiscovered, 'recentDiscovered');
            }
        } catch (error) {}
    };

    const getCategories = async () => {
        const result = await researchUI({
            function: 'getAllCategories',
        });
        AppCategoryList = {};
        AppTypes.category.options = result.data.map((cat) => {
            AppCategoryList[cat.category_id] = cat.category_name;
            return { label: cat.category_name, value: cat.category_name };
        });
        fetchAppIds();
    };

    const appNameAutocomplete = async (text) => {
        const result = await researchUI({
            function: 'appNameAutocomplete',
            text,
        });
        setAppOptions(
            result.data.map((row) => {
                return { label: row.current_name, value: row.application_id };
            })
        );
    };

    const submitGptReq = async (appIds, attributes, customQues, isCustom = false) => {
        setReqLoading(true);
        const data = { app_ids: appIds.map((ids) => ids.toString()) };
        if (isCustom) {
            data['query'] = typeof customQues === TYPE_OF.STRING ? [customQues] : customQues;
        } else {
            data['attr_list'] = attributes;
        }
        const postData = { method: API_METHODS.POST, uri: isCustom ? 'custom' : 'attribute', data };
        try {
            const result = await cciGptService(postData);
            if (result.success == COMMON_NUMBER.API_FAILED) message.error(result.data.msg, 4);
            if (result.data.status_code !== STATUS_CODE.OK) message.error(ERROR_MSG.GENERAL_ERROR, 4);
            if (result.success === COMMON_NUMBER.API_SUCCESS) {
                setResultData(result.data);
                setShowResult(true);
            }
            setReqLoading(false);
        } catch (error) {
            setReqLoading(false);
        }
    };

    useEffect(() => {
        getCategories();
        fetchInitData();
        setAppTypeOption(
            Object.entries(AppTypes).map(([key, value]) => {
                return { label: value.name, value: key };
            })
        );
    }, []);

    useEffect(() => {
        setShowQuesSel(selApps.length > COMMON_NUMBER.LENGTH_ZERO);
    }, [selApps]);

    useEffect(() => {
        if (isCustomQues) {
            setSelQuestions(defaultValues.questions);
        } else {
            setCustomQues(defaultValues.customQues);
        }
    }, [isCustomQues]);

    useEffect(() => {
        if (!selAppType) return;
        const appType = AppTypes[selAppType];
        setAppOptions(appType.options);
        setAppSelectPlaceholder(appType.placeholder);
        setShowSwitch(appType.customQues);
        resetFields();
    }, [selAppType]);

    const resetFields = () => {
        setSelApps(defaultValues.apps);
        setIsCustomQues(defaultValues.isCustomQues);
        setCustomQues(defaultValues.customQues);
        setSelQuestions(defaultValues.questions);
    };

    const onSelectAppType = ({ target: { value } }) => {
        setSelAppType(value);
    };

    const onSelectApp = (values, options) => {
        setSelApps(values);
        options.map((option) => {
            AppIdNameMapping[option.value] = option.label;
        });
    };

    const onSearchApp = (value) => {
        if (AppTypes[selAppType]?.fetchDropdown) {
            if (value && value.length > 2) {
                appNameAutocomplete(value);
            }
        }
    };

    const onChangeQuestionType = (checked) => {
        setIsCustomQues(checked);
    };

    const onSelectQuestionCategories = (values) => {
        if (selQuestions.includes(AllOption.value)) {
            if (values.includes(AllOption.value)) {
                values = values.filter((value) => value !== AllOption.value);
            }
        }
        setSelQuestions(values);
    };

    const onSelectQuestion = (value) => {
        if (value === AllOption.value) {
            setSelQuestions([AllOption.value]);
        }
    };

    const onChangeQuestionTree = (value, label, extra) => {
        setSelQuestions(value);
    };

    const onSelectQuestionTree = (value, node, extra) => {
        console.log('Tree Select : ', value, node, extra);
    };

    const onChangeCustomQuestion = (e) => {
        setCustomQues(e.target.value);
    };

    const showSubmitButton = () => {
        if (selQuestions.length > COMMON_NUMBER.LENGTH_ZERO || customQues.length > COMMON_NUMBER.LENGTH_ZERO) {
            return false;
        }
        return true;
    };

    const onSubmit = () => {
        if (selApps.length === COMMON_NUMBER.LENGTH_ZERO) {
            message.error(NOTICATION_MSG.CCI_GPT_SELECT_APPS);
            return;
        }
        if (selQuestions.length === COMMON_NUMBER.LENGTH_ZERO && !isCustomQues) {
            message.error(NOTICATION_MSG.CCI_GPT_SELECT_QUESTION);
            return;
        }
        if (customQues.length === COMMON_NUMBER.LENGTH_ZERO && isCustomQues) {
            message.error(NOTICATION_MSG.CCI_GPT_CUSTOM_QUESTION);
            return;
        }
        let attributes = [];
        selQuestions.map((attribute) => {
            if (Object.keys(CategoryQuestionMapping).includes(attribute)) {
                attributes = attributes.concat(CategoryQuestionMapping[attribute]);
            } else {
                attributes.push(attribute);
            }
        });
        if (AppTypes[selAppType].isProcess && !selQuestions.includes(AllOption.value) && attributes.length <= MinBulkLimit) {
            console.log('Attributes : ', attributes);
            submitGptReq(selApps, attributes, customQues, isCustomQues);
        } else {
            notification.success({
                message: 'Bulk Processing Request',
                description:
                    'Your request has been sent to the backend pipeline. You will be notified via email once the results are ready.',
                placement: 'top',
            });
            resetFields();
        }
        setResultData(null);
        setShowResult(false);
    };

    const onClickUrlSearch = () => {
        setSearchModalOpen(true);
    };

    const onCloseModal = () => {
        setSearchModalOpen(false);
    };

    return (
        <>
            {loading ? (
                <Loading />
            ) : (
                <>
                    <div className="container">
                        <Space direction={ANTD_SPACE.DIRECTION_VERTICAL} className="ns-width-full">
                            <Radio.Group
                                className="ns-flex ns-flex_centered"
                                options={appTypeOption}
                                onChange={onSelectAppType}
                                optionType="button"
                                buttonStyle="solid"
                                defaultValue={AppTypes.existing.key}
                            />
                            <Select
                                className="ns-margin-top-small"
                                mode={ANTD_SELECT.MODE_MULTI}
                                allowClear
                                style={{ width: '100%' }}
                                placeholder={appSelectPlaceholder}
                                defaultActiveFirstOption={false}
                                showArrow={true}
                                filterOption={
                                    AppTypes[selAppType]?.filterOption
                                        ? (input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        : false
                                }
                                // onSearch={onSearchApp}
                                onChange={onSelectApp}
                                options={appOptions}
                                value={selApps}
                            />
                            {showSwitch ? (
                                <Space size={2} align={ANTD_SPACE.ALIGN_BASELINE} className="ns-margin-top-small ns-width-full">
                                    <Switch size={SIZE.SMALL} onChange={onChangeQuestionType} />
                                    <Text type={ANTD_TEXT.TYPE_SECONDARY}>Do you want to ask custom question ?</Text>
                                    {selApps.length === 1 ? (
                                        <Button
                                            className="ns-margin-left"
                                            type="primary"
                                            shape={'round'}
                                            size={'small'}
                                            onClick={onClickUrlSearch}
                                        >
                                            Search URL / Text
                                        </Button>
                                    ) : (
                                        ''
                                    )}
                                </Space>
                            ) : null}
                            {isCustomQues ? (
                                <Input
                                    className="ns-margin-top-small"
                                    placeholder={PLACEHOLDER_TEXT.CCI_GPT_CUSTOM_QUESTION}
                                    onChange={onChangeCustomQuestion}
                                />
                            ) : (
                                <TreeSelect
                                    className="ns-width-full"
                                    treeData={attriTreeData}
                                    treeCheckable={true}
                                    showCheckedStrategy={SHOW_PARENT}
                                    placeholder={PLACEHOLDER_TEXT.CCI_GPT_SELECT_QUESTION}
                                    onChange={onChangeQuestionTree}
                                    onSelect={onSelectQuestionTree}
                                    value={selQuestions}
                                />
                            )}

                            <div className="ns-width-full ns-flex ns-flex_centered">
                                <Button
                                    size={SIZE.SMALL}
                                    type={ANTD_BUTTON.TYPE_PRIMARY}
                                    shape={ANTD_BUTTON.SHAPE_ROUND}
                                    className="ns-margin-top-small"
                                    onClick={onSubmit}
                                    disabled={showSubmitButton()}
                                >
                                    {COMMON_STRING.SUBMIT}
                                </Button>
                            </div>
                        </Space>
                    </div>
                    {reqLoading ? <Loading /> : null}
                    {showResult ? <ResultView resultData={resultData} showResult={setShowResult} /> : null}
                </>
            )}
            <Modal
                title="Cci-Gpt Search"
                open={searchModalOpen}
                width={'80%'}
                onCancel={onCloseModal}
                destroyOnClose
                okButtonProps={{ hidden: true }}
            >
                <CciGptSearch appId={selApps} />
            </Modal>
        </>
    );
};

const Loading = () => {
    return (
        <div className="container ns-flex_center">
            <Spin size={SIZE.LARGE} />
        </div>
    );
};

const ResultView = (props) => {
    const defaultColumn = [
        {
            title: 'App',
            dataIndex: 'appName',
            key: 'appName',
            width: '10%',
        },
    ];

    const [data, setData] = useState(props.resultData?.data);
    const [tableColumn, setTableColumn] = useState(defaultColumn);
    const [tableData, setTableData] = useState([]);
    useEffect(() => {
        if (!data) return;
        const column = new Set();
        const tableCol = defaultColumn;
        const appData = [];

        Object.entries(data).map(([appId, app]) => {
            const quesData = {};
            quesData['appName'] = AppIdNameMapping[appId];
            app.map((ques) => {
                column.add(ques.name);
                quesData[ques.name] = ques.questions;
            });
            appData.push(quesData);
        });

        for (const col of column) {
            tableCol.push({
                title: QuesSummaryMapping.hasOwnProperty(col) ? QuesSummaryMapping[col] : col,
                dataIndex: col,
                key: col,
                width: 90 / column.size + '%',
                render: (questions) => <AttributeColumn attributeName={col} questions={questions} />,
            });
        }
        setTableColumn(tableCol);
        setTableData(appData);
    }, []);

    const onClickClose = () => {
        props.showResult(false);
    };
    return (
        <>
            <div key={'tab'} className="container">
                <CloseOutlined className="ns-flex ns-flex_end" onClick={onClickClose} />
                <Table
                    key={'result'}
                    size={SIZE.SMALL}
                    pagination={false}
                    columns={tableColumn}
                    dataSource={tableData}
                    rowClassName={styles.tableRow}
                />
            </div>
        </>
    );
};

const AttributeColumn = (props) => {
    const [data, setData] = useState(props.questions ?? []);
    const [showModal, setShowModal] = useState(false);
    const [modalData, setModalData] = useState([]);

    const openModal = (data) => {
        setModalData(data);
        setShowModal(true);
    };
    const onCancelModal = () => {
        setShowModal(false);
    };
    return (
        <>
            <Space size={8} direction={ANTD_SPACE.DIRECTION_VERTICAL}>
                {data.map((question, index) => {
                    return (
                        <Space key={props.attributeName + '_' + index + '_space'} size={2} direction={ANTD_SPACE.DIRECTION_VERTICAL}>
                            <Text type={ANTD_TEXT.TYPE_SECONDARY}>{question.question}</Text>
                            <div>
                                <Text type={question.answer ? ANTD_TEXT.TYPE_SUCCESS : ANTD_TEXT.TYPE_WARNING}>
                                    {question.answer ?? COMMON_MSG.NO_ANSWER}
                                </Text>
                                {question.answer ? (
                                    <InfoCircleFilled style={{ color: 'grey' }} onClick={() => openModal(question)} />
                                ) : null}
                            </div>
                        </Space>
                    );
                })}
            </Space>
            <Modal
                title="Proof of answers"
                open={showModal}
                onCancel={onCancelModal}
                okButtonProps={{ hidden: true }}
                cancelButtonProps={{ hidden: true }}
                width={'70%'}
            >
                <SourceModal data={modalData} />
            </Modal>
        </>
    );
};

const SourceModal = (props) => {
    const column = [
        {
            title: 'Source',
            dataIndex: 'text',
            key: 'text',
            render: (text) => <Text type={ANTD_TEXT.TYPE_SECONDARY}>{text}</Text>,
        },
        {
            title: 'Url',
            dataIndex: 'url',
            key: 'url',
            width: '30%',
            render: (url) => (
                <a target={'_blank'} href={url}>
                    {url}
                </a>
            ),
        },
    ];
    const [tableData, setTableData] = useState([]);
    useEffect(() => {
        setTableData(props.data.sources);
    }, []);
    return <Table size={SIZE.SMALL} pagination={false} columns={column} dataSource={tableData} bordered tableLayout={'fixed'} />;
};

export default CciGpt;
